import { PopoverTrigger } from '@radix-ui/react-popover';
import format from 'date-fns/format';
import { CalendarIcon, ChevronDown, X } from 'lucide-react';
import { useState } from 'react';

import { Calendar } from './Calendar';
import { Input } from './input';
import { Popover, PopoverContent } from './popover';
import { InputVariants, inputVariants } from './variants';

interface DateTimePickerProps extends InputVariants {
  dateTime?: Date;
  onDateTimeChange: (dateTime?: Date) => void;
  className?: string;
  placeholder?: string;
}

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  dateTime,
  onDateTimeChange,
  className,
  placeholder = 'Select date and time',
  color,
  size,
  variant,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);

  const handleDateChange = (date?: Date) => {
    if (!date) {
      onDateTimeChange(undefined);
      return;
    }

    const newDateTime = new Date(date);
    if (dateTime) {
      newDateTime.setHours(dateTime.getHours());
      newDateTime.setMinutes(dateTime.getMinutes());
    }
    onDateTimeChange(newDateTime);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parts = e.target.value.split(':');
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);

    if (isNaN(hours) || isNaN(minutes)) return;

    const newDateTime = dateTime ? new Date(dateTime) : new Date();
    newDateTime.setHours(hours);
    newDateTime.setMinutes(minutes);
    newDateTime.setSeconds(0);
    newDateTime.setMilliseconds(0);
    onDateTimeChange(newDateTime);
  };

  const timeValue = dateTime
    ? `${String(dateTime.getHours()).padStart(2, '0')}:${String(dateTime.getMinutes()).padStart(2, '0')}`
    : '';

  const handleClear = () => {
    onDateTimeChange(undefined);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={inputVariants({
            className: [
              'flex min-w-0 cursor-pointer items-center justify-between gap-2',
              className,
            ],
            color,
            size,
            variant,
            disabled,
          })}
        >
          <CalendarIcon size={16} />
          {dateTime ? (
            format(dateTime, 'PPP p')
          ) : (
            <span className="text-text-tertiary">{placeholder}</span>
          )}
          <ChevronDown className="ml-auto" size={16} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={dateTime}
          defaultMonth={dateTime}
          onSelect={handleDateChange}
          initialFocus
        />
        <div className="flex items-center justify-between border-t border-border-primary p-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-text-tertiary">Time:</span>
            <Input
              type="time"
              value={timeValue}
              onChange={handleTimeChange}
              className="w-auto"
            />
          </div>
          {dateTime && (
            <button
              type="button"
              onClick={handleClear}
              className="flex items-center gap-1 rounded px-2 py-1 text-sm text-text-tertiary hover:bg-bg-quaternary hover:text-text-secondary"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
